import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { TwitterSearchResult } from '@/types/twitterTypes';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { format } from 'date-fns';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { Search, Check, Info, ExternalLink } from 'lucide-react';
import { ScrollArea } from '@/components/ui/scroll-area';
import { cn } from '@/lib/utils';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { useNavigate } from 'react-router-dom';

interface TweetKolTrackerDialogProps {
  tweet: TwitterSearchResult;
  isOpen: boolean;
  onClose: () => void;
}

interface KolOption {
  id: string;
  name: string;
  social_media_handler: string;
}

const POST_TYPE_OPTIONS = ['Airdrop', 'Defi', 'General'];
const CAMPAIGN_BU_OPTIONS = [
  'Event - Rewards Station', 
  'Token - MNT Token Mindshare', 
  'BU - FBTC',
  'BU - Mantle Network (includes L2 Eco Dapps)', 
  'BU - mETH (includes COOK & cmETH)',
  'BU - Index Fund',
  'BU - Mantle Banking',
  'BU - Mantle Group',
  'BU - MantleX',
  'Others'
];

const TweetKolTrackerDialog: React.FC<TweetKolTrackerDialogProps> = ({ tweet, isOpen, onClose }) => {
  const navigate = useNavigate();
  
  const [kolOptions, setKolOptions] = useState<KolOption[]>([]);
  const [filteredKolOptions, setFilteredKolOptions] = useState<KolOption[]>([]);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [selectedKolId, setSelectedKolId] = useState<string>('');
  const [postType, setPostType] = useState<string>('General');
  const [campaignBu, setCampaignBu] = useState<string>(CAMPAIGN_BU_OPTIONS[0]);
  const [expense, setExpense] = useState<string>('0');
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [showKolDropdown, setShowKolDropdown] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    const fetchKolOptions = async () => {
      setIsLoading(true);
      try {
        // First try to find KOL by username match
        const username = tweet.author_username;
        if (username) {
          const cleanUsername = username.startsWith('@') ? username.substring(1) : username;
          
          const { data: kolData, error } = await supabase
            .from('kol_db')
            .select('id, creator_name, social_media_handler')
            .or(`social_media_handler.eq.${cleanUsername},social_media_handler.eq.@${cleanUsername}`)
            .eq('social_media_type', 'Twitter');
          
          if (error) throw error;
          
          if (kolData && kolData.length > 0) {
            // Map the data to have name property instead of creator_name
            const mappedData = kolData.map(kol => ({
              id: kol.id,
              name: kol.creator_name, // Map creator_name to name
              social_media_handler: kol.social_media_handler
            }));
            setKolOptions(mappedData);
            setFilteredKolOptions(mappedData);
            // Auto-select the first match if exact match found
            setSelectedKolId(mappedData[0].id);
          } else {
            // Fetch all KOLs if no direct match
            const { data: allKols, error: allKolsError } = await supabase
              .from('kol_db')
              .select('id, creator_name, social_media_handler')
              .eq('social_media_type', 'Twitter');
            
            if (allKolsError) throw allKolsError;
            
            const mappedAllKols = allKols?.map(kol => ({
              id: kol.id,
              name: kol.creator_name, // Map creator_name to name
              social_media_handler: kol.social_media_handler
            })) || [];
            
            setKolOptions(mappedAllKols);
            setFilteredKolOptions(mappedAllKols);
          }
        }
      } catch (error) {
        console.error('Error fetching KOL options:', error);
        toast({
          title: 'Error',
          description: 'Failed to load KOL options',
          variant: 'destructive'
        });
      } finally {
        setIsLoading(false);
      }
    };
    
    if (isOpen) {
      fetchKolOptions();
    }
  }, [isOpen, tweet, toast]);

  // Filter KOL options based on search term
  useEffect(() => {
    if (searchTerm.trim() === '') {
      setFilteredKolOptions(kolOptions);
    } else {
      const lowerSearchTerm = searchTerm.toLowerCase();
      const filtered = kolOptions.filter(kol => 
        kol.name.toLowerCase().includes(lowerSearchTerm) || 
        (kol.social_media_handler && kol.social_media_handler.toLowerCase().includes(lowerSearchTerm))
      );
      setFilteredKolOptions(filtered);
    }
  }, [searchTerm, kolOptions]);

  const handleSave = async () => {
    if (!selectedKolId) {
      toast({
        title: 'Error',
        description: 'Please select a KOL',
        variant: 'destructive'
      });
      return;
    }
    
    setIsSaving(true);
    try {
      // Find the selected KOL information
      const selectedKol = kolOptions.find(kol => kol.id === selectedKolId);
      if (!selectedKol) throw new Error('Selected KOL not found');
      
      // Create new entry in kol_tracker
      const { error } = await supabase
        .from('kol_tracker')
        .insert({
          name: selectedKol.name,
          kol_db_id: selectedKolId,
          channel: selectedKol.social_media_handler,
          platform: 'Twitter',
          post_type: postType,
          post_link: `https://twitter.com/i/web/status/${tweet.tweet_id}`,
          campaign_bu: campaignBu,
          expense: expense ? parseFloat(expense) : 0,
          content_views: tweet.impression_count || 0,
          posting_date: tweet.created_at ? new Date(tweet.created_at).toISOString().split('T')[0] : null,
          integration_enable: true,
          type: 'Twitter - Tweet',
        });
      
      if (error) throw error;
      
      toast({
        title: 'Success',
        description: 'Tweet added to KOL Tracker successfully',
      });
      
      onClose();
    } catch (error) {
      console.error('Error saving to KOL Tracker:', error);
      toast({
        title: 'Error',
        description: 'Failed to add tweet to KOL Tracker',
        variant: 'destructive'
      });
    } finally {
      setIsSaving(false);
    }
  };
  
  const handleKolSearchInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
  };

  const handleKolSelect = (id: string) => {
    setSelectedKolId(id);
    setShowKolDropdown(false);
  };

  const selectedKolName = selectedKolId ? 
    kolOptions.find(kol => kol.id === selectedKolId)?.name + 
    (kolOptions.find(kol => kol.id === selectedKolId)?.social_media_handler ? 
      ` (@${kolOptions.find(kol => kol.id === selectedKolId)?.social_media_handler?.replace('@', '')})` : '') : 
    'Select KOL';

  const handleNavigateToDataImport = () => {
    onClose();
    navigate('/kol-tracker?tab=data-import-v2');
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Add Tweet to KOL Tracker</DialogTitle>
        </DialogHeader>

        {/* Data Import Announcement */}
        <Alert className="mb-4 border-blue-200 bg-blue-50 dark:border-blue-800 dark:bg-blue-900/20">
          <Info className="h-4 w-4 text-blue-600 dark:text-blue-400" />
          <AlertDescription className="text-blue-700 dark:text-blue-300">
            <div className="flex items-start justify-between gap-2">
              <div>
                <strong>New Feature Available!</strong> Use our enhanced Data Import functionality to easily import multiple entries at once instead of adding them one by one.
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={handleNavigateToDataImport}
                className="shrink-0 text-blue-600 border-blue-300 hover:bg-blue-100 dark:text-blue-400 dark:border-blue-600 dark:hover:bg-blue-900/30"
              >
                Try Import <ExternalLink className="ml-1 h-3 w-3" />
              </Button>
            </div>
          </AlertDescription>
        </Alert>
        
        <div className="grid gap-4 py-4">
          <div className="grid gap-2">
            <Label htmlFor="kol">KOL</Label>
            <div className="relative">
              <div className="relative w-full">
                <button
                  type="button"
                  onClick={() => setShowKolDropdown(!showKolDropdown)}
                  className={cn(
                    "flex h-10 w-full items-center justify-between rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background",
                    "placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2",
                    "disabled:cursor-not-allowed disabled:opacity-50"
                  )}
                  disabled={isLoading}
                >
                  <span className="truncate">{selectedKolName}</span>
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className={cn(
                      "h-4 w-4 transition-transform",
                      showKolDropdown ? "rotate-180" : ""
                    )}
                  >
                    <polyline points="6 9 12 15 18 9"></polyline>
                  </svg>
                </button>
                
                {showKolDropdown && (
                  <div className="absolute z-[99999] w-full mt-1 bg-white border rounded-md shadow-lg" style={{ pointerEvents: 'auto' }}>
                    <div className="p-2 border-b">
                      <div className="flex items-center border rounded-md">
                        <Search className="h-4 w-4 mx-2 text-gray-400" />
                        <Input
                          placeholder="Search KOLs..."
                          value={searchTerm}
                          onChange={handleKolSearchInputChange}
                          className="border-0 focus-visible:ring-0 focus-visible:ring-offset-0"
                          style={{ pointerEvents: 'auto' }}
                        />
                      </div>
                    </div>
                    <ScrollArea className="max-h-[300px] overflow-auto" style={{ pointerEvents: 'auto' }}>
                      <div className="p-1" style={{ pointerEvents: 'auto' }}>
                        {filteredKolOptions.length > 0 ? (
                          filteredKolOptions.map(kol => (
                            <div
                              key={kol.id}
                              className={cn(
                                "flex items-center justify-between px-3 py-2.5 cursor-pointer rounded-sm text-sm", // Increased padding
                                selectedKolId === kol.id ? "bg-blue-50 text-blue-600" : "hover:bg-gray-100"
                              )}
                              onClick={() => handleKolSelect(kol.id)}
                              style={{ pointerEvents: 'auto' }}
                            >
                              <div>
                                <span className="font-medium">{kol.name}</span>
                                {kol.social_media_handler && (
                                  <span className="text-gray-500 ml-1">
                                    (@{kol.social_media_handler.replace('@', '')})
                                  </span>
                                )}
                              </div>
                              {selectedKolId === kol.id && <Check className="h-4 w-4 text-blue-600" />}
                            </div>
                          ))
                        ) : (
                          <div className="text-center py-4 text-sm text-gray-500">
                            {isLoading ? 'Loading...' : 'No KOLs found'}
                          </div>
                        )}
                      </div>
                    </ScrollArea>
                  </div>
                )}
              </div>
            </div>
          </div>
          
          <div className="grid gap-2">
            <Label htmlFor="postType">Post Type</Label>
            <Select value={postType} onValueChange={setPostType}>
              <SelectTrigger>
                <SelectValue placeholder="Select post type" />
              </SelectTrigger>
              <SelectContent>
                {POST_TYPE_OPTIONS.map(option => (
                  <SelectItem key={option} value={option}>{option}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          
          <div className="grid gap-2">
            <Label htmlFor="campaignBu">Campaign BU</Label>
            <Select value={campaignBu} onValueChange={setCampaignBu}>
              <SelectTrigger>
                <SelectValue placeholder="Select campaign BU" />
              </SelectTrigger>
              <SelectContent>
                {CAMPAIGN_BU_OPTIONS.map(option => (
                  <SelectItem key={option} value={option}>{option}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          
          <div className="grid gap-2">
            <Label htmlFor="expense">Expense ($)</Label>
            <Input
              id="expense"
              type="number"
              value={expense}
              onChange={(e) => setExpense(e.target.value)}
              min="0"
              step="0.01"
            />
          </div>
          
          <div className="grid gap-2">
            <Label>Tweet Info</Label>
            <div className="p-3 bg-muted rounded-md text-sm">
              <div><span className="font-semibold">Author:</span> {tweet.author_name} (@{tweet.author_username})</div>
              <div><span className="font-semibold">Date:</span> {tweet.created_at ? format(new Date(tweet.created_at), 'PPP') : 'Unknown'}</div>
              <div><span className="font-semibold">Impressions:</span> {tweet.impression_count || 'Unknown'}</div>
            </div>
          </div>
        </div>
        
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={isSaving}>Cancel</Button>
          <Button onClick={handleSave} disabled={isLoading || isSaving || !selectedKolId}>
            {isSaving ? 'Saving...' : 'Save to KOL Tracker'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default TweetKolTrackerDialog;
